/**
 * Aria & Co. — rich fixture data for every non-Timeline surface.
 *
 * TimelineContext already reads elements + checklists from the base seed via
 * `getExploreWorkspaceSeedByWorkspaceId`. This module bundles everything else
 * (chat, uploads, budgets, quotas, stock, forms, meetings, automations,
 * members roster) so a viewer sees a fully-populated product on EVERY tab.
 */

import type { Timestamp } from 'firebase/firestore'
import type { Channel, Message, WorkspaceQuota } from '../types/chat'
import type { WorkspaceBudgetSummary } from '../types/budget'
import type { Quota } from '../types/quota'
import type { StockItem } from '../types/stock'
import type { MeetingRecord } from '../types/meetings'
import type { FormDefinition, FormResponse } from '../types/forms'
import type { AutomationDefinition, AutomationRun } from '../types/automation'
import type { WorkspaceFileMetadata, WorkspaceStorageQuota } from '../types/schema'
import type { WorkspaceUserDirectoryEntry } from '../services/workspaceUserDirectory'

const WORKSPACE_ID = 'explore-seed-aria-and-co'

const daysFromNow = (days: number): Date => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d
}

/** Build a fake Firestore Timestamp that satisfies the read-side APIs
 * (toDate/toMillis/seconds/nanoseconds) without a real Firestore write. */
export const mockTimestamp = (source: Date | number | string): Timestamp => {
  const millis = source instanceof Date
    ? source.getTime()
    : typeof source === 'number'
      ? source
      : new Date(source).getTime()
  return {
    seconds: Math.floor(millis / 1000),
    nanoseconds: 0,
    toDate: () => new Date(millis),
    toMillis: () => millis,
    isEqual: () => false,
    toJSON: () => ({ seconds: Math.floor(millis / 1000), nanoseconds: 0, type: 'timestamp' }),
    valueOf: () => String(millis),
  } as unknown as Timestamp
}

// ---------------------------------------------------------------------------
// Members roster — shared by Chat + Meetings surfaces
// ---------------------------------------------------------------------------

export type DemoMember = WorkspaceUserDirectoryEntry & {
  role: 'ADMIN' | 'EDITOR' | 'VIEWER' | 'READONLY' | 'HELPER'
}

export const DEMO_MEMBERS: DemoMember[] = [
  { membershipId: 'm-aria', uid: 'aria-founder', displayName: 'Aria Chen', email: 'aria@example.test', role: 'ADMIN', avatarUrl: '' },
  { membershipId: 'm-kai', uid: 'kai-lead', displayName: 'Kai Ramirez', email: 'kai@example.test', role: 'EDITOR', avatarUrl: '' },
  { membershipId: 'm-jules', uid: 'jules-designer', displayName: 'Jules Park', email: 'jules@example.test', role: 'EDITOR', avatarUrl: '' },
  { membershipId: 'm-sam', uid: 'sam-ops', displayName: 'Sam Osei', email: 'sam@example.test', role: 'EDITOR', avatarUrl: '' },
  { membershipId: 'm-noor', uid: 'noor-accounts', displayName: 'Noor Habib', email: 'noor@example.test', role: 'EDITOR', avatarUrl: '' },
  { membershipId: 'm-devi', uid: 'devi-jr', displayName: 'Devi Sharma', email: 'devi@example.test', role: 'VIEWER', avatarUrl: '' },
  { membershipId: 'm-marcus', uid: 'marcus-jr', displayName: 'Marcus Lee', email: 'marcus@example.test', role: 'VIEWER', avatarUrl: '' },
  { membershipId: 'm-petra', uid: 'petra-jr', displayName: 'Petra Okafor', email: 'petra@example.test', role: 'VIEWER', avatarUrl: '' },
  { membershipId: 'm-yuki', uid: 'yuki-h', displayName: 'Yuki Tanaka', email: 'yuki@example.test', role: 'HELPER', avatarUrl: '' },
  { membershipId: 'm-rafael', uid: 'rafael-h', displayName: 'Rafael Diaz', email: 'rafael@example.test', role: 'HELPER', avatarUrl: '' },
  { membershipId: 'm-emma', uid: 'emma-h', displayName: 'Emma Larsen', email: 'emma@example.test', role: 'HELPER', avatarUrl: '' },
  { membershipId: 'm-aya', uid: 'aya-h', displayName: 'Aya Fadel', email: 'aya@example.test', role: 'HELPER', avatarUrl: '' },
]

// ---------------------------------------------------------------------------
// Chat channels + messages
// ---------------------------------------------------------------------------

const buildChannel = (input: {
  id: string
  name: string
  slug: string
  type?: 'public' | 'private'
  lastActivity: Date
}): Channel => ({
  id: input.id,
  name: input.name,
  slug: input.slug,
  type: input.type ?? 'public',
  roles: {},
  lastActivity: mockTimestamp(input.lastActivity),
  lastReadAt: {},
  createdBy: 'aria-founder',
  createdAt: mockTimestamp(daysFromNow(-180)),
})

export const DEMO_CHANNELS: Channel[] = [
  buildChannel({ id: 'ch-general', name: 'general', slug: 'general', lastActivity: daysFromNow(-0.02) }),
  buildChannel({ id: 'ch-vendors', name: 'vendors', slug: 'vendors', lastActivity: daysFromNow(-0.5) }),
  buildChannel({ id: 'ch-chen-rivera', name: 'chen-rivera', slug: 'chen-rivera', lastActivity: daysFromNow(-1) }),
  buildChannel({ id: 'ch-design', name: 'design', slug: 'design', lastActivity: daysFromNow(-1.5) }),
  buildChannel({ id: 'ch-performance', name: 'performance', slug: 'performance', lastActivity: daysFromNow(-3) }),
  buildChannel({ id: 'ch-day-of-runs', name: 'day-of-runs', slug: 'day-of-runs', type: 'private', lastActivity: daysFromNow(-4) }),
]

const buildMessage = (input: {
  id: string
  channelId: string
  author: DemoMember
  text: string
  hoursAgo: number
  replyToId?: string
}): Message & { channelId: string } => ({
  id: input.id,
  channelId: input.channelId,
  authorId: input.author.uid,
  authorName: input.author.displayName,
  authorAvatarUrl: '',
  authorRole: input.author.role === 'ADMIN' ? 'ADMIN'
    : input.author.role === 'EDITOR' || input.author.role === 'VIEWER' ? 'MEMBER'
    : input.author.role === 'HELPER' ? 'HELPER' : 'VIEWER',
  textContent: input.text,
  timestamp: mockTimestamp(new Date(Date.now() - input.hoursAgo * 3600 * 1000)),
  replyToId: input.replyToId,
})

const [ARIA, KAI, JULES, SAM, NOOR, DEVI, MARCUS, , , RAFAEL, EMMA] = DEMO_MEMBERS

export const DEMO_MESSAGES: Array<Message & { channelId: string }> = [
  buildMessage({ id: 'msg-g-1', channelId: 'ch-general', author: ARIA, hoursAgo: 72,
    text: '🎉 Welcome to the Aria & Co. demo workspace. Take a look around — this is what a fully-set-up team looks like six months into wedding season.' }),
  buildMessage({ id: 'msg-g-2', channelId: 'ch-general', author: KAI, hoursAgo: 71,
    text: 'Reminder: vendor payment approvals for Chen×Rivera are due Friday. I moved the checklist to the top of the board.' }),
  buildMessage({ id: 'msg-g-3', channelId: 'ch-general', author: JULES, hoursAgo: 60,
    text: 'Uploaded the second-draft mood board for Patel×Sharma — link in #design. Feedback welcome by EOW.' }),
  buildMessage({ id: 'msg-g-4', channelId: 'ch-general', author: SAM, hoursAgo: 48,
    text: 'Weekly digest: 6 active weddings, 42 checklists, $116k spent of $455k budget. On pace.' }),
  buildMessage({ id: 'msg-g-5', channelId: 'ch-general', author: NOOR, hoursAgo: 24,
    text: 'Guest RSVPs for Moretti×Kim jumped 40% this week — hotel block getting close to filled.' }),
  buildMessage({ id: 'msg-g-6', channelId: 'ch-general', author: ARIA, hoursAgo: 4,
    text: 'Coffee-catering for Friday standup is on me. Post food orders in-thread ☕' }),
  buildMessage({ id: 'msg-g-7', channelId: 'ch-general', author: DEVI, hoursAgo: 3, replyToId: 'msg-g-6',
    text: 'Oat milk latte 🙏' }),
  buildMessage({ id: 'msg-g-8', channelId: 'ch-general', author: MARCUS, hoursAgo: 2, replyToId: 'msg-g-6',
    text: 'Regular drip, black. Thanks Aria!' }),

  buildMessage({ id: 'msg-v-1', channelId: 'ch-vendors', author: KAI, hoursAgo: 96,
    text: 'Contract signed with Meridian Florals — 15% seasonal discount held for Sep + Oct events.' }),
  buildMessage({ id: 'msg-v-2', channelId: 'ch-vendors', author: SAM, hoursAgo: 60,
    text: 'DJ backup for Liu×Anders — auditioned two more; Kai and I liked "Loop Culture" best.' }),
  buildMessage({ id: 'msg-v-3', channelId: 'ch-vendors', author: NOOR, hoursAgo: 30,
    text: '⚠️ Napa Chateau deposit invoice is 3 days overdue. I sent a nudge, will call tomorrow if no reply.' }),
  buildMessage({ id: 'msg-v-4', channelId: 'ch-vendors', author: JULES, hoursAgo: 12,
    text: 'Photographer for García×Nakamura pulled out. Options in the checklist — need a decision by Monday.' }),

  buildMessage({ id: 'msg-c-1', channelId: 'ch-chen-rivera', author: ARIA, hoursAgo: 36,
    text: 'Final walkthrough of the Golden Gate Club is Wed at 3pm. Kai + Jules please attend.' }),
  buildMessage({ id: 'msg-c-2', channelId: 'ch-chen-rivera', author: KAI, hoursAgo: 24,
    text: 'Confirmed. I have the day-of run sheet 90% done — will share tomorrow.' }),
  buildMessage({ id: 'msg-c-3', channelId: 'ch-chen-rivera', author: JULES, hoursAgo: 12,
    text: 'Ceremony floral install: 6am–9am. Reception setup: 11am–2pm. Team of 5, coffee ordered.' }),

  buildMessage({ id: 'msg-d-1', channelId: 'ch-design', author: JULES, hoursAgo: 40,
    text: 'Mood board V2 for Patel×Sharma is on the Design board. Palette: deep saffron, ivory, forest green.' }),
  buildMessage({ id: 'msg-d-2', channelId: 'ch-design', author: EMMA, hoursAgo: 22,
    text: 'Love the palette. Question on invitation stock — matte or slight sheen?' }),
  buildMessage({ id: 'msg-d-3', channelId: 'ch-design', author: JULES, hoursAgo: 8, replyToId: 'msg-d-2',
    text: 'Matte, with letterpressed script. Cream envelopes lined with saffron tissue.' }),

  buildMessage({ id: 'msg-p-1', channelId: 'ch-performance', author: ARIA, hoursAgo: 168,
    text: 'End-of-month quotas posted. Everyone hit sales_units targets — nice work. Q4 targets next week.' }),
  buildMessage({ id: 'msg-p-2', channelId: 'ch-performance', author: RAFAEL, hoursAgo: 96,
    text: 'Bump on tasks_completed this cycle — proud of the team.' }),

  buildMessage({ id: 'msg-do-1', channelId: 'ch-day-of-runs', author: ARIA, hoursAgo: 200,
    text: 'Private channel for day-of coordination only. Radio frequencies, contact list, and emergency escalation live here.' }),
  buildMessage({ id: 'msg-do-2', channelId: 'ch-day-of-runs', author: KAI, hoursAgo: 100,
    text: 'Radio ch. 4 for wedding party, ch. 5 for vendors, ch. 6 emergencies. Backup phones charged Friday PM.' }),
]

export const DEMO_WORKSPACE_QUOTA: WorkspaceQuota = {
  limit: 500,
  usedToday: 47,
  dayKey: '2026-10-19',
  isOverQuota: false,
}

// ---------------------------------------------------------------------------
// Uploads
// ---------------------------------------------------------------------------

const buildFile = (input: {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  uploader: DemoMember
  daysAgo: number
  visibility?: 'PRIVATE' | 'WORKSPACE' | 'PUBLIC'
  artifactType?: string
  sourceChatTitle?: string
}): WorkspaceFileMetadata => ({
  id: input.id,
  workspaceId: WORKSPACE_ID,
  fileName: input.fileName,
  source: 'NATIVE' as never,
  status: 'READY' as never,
  visibility: (input.visibility ?? 'WORKSPACE') as never,
  sizeBytes: input.sizeBytes,
  mimeType: input.mimeType,
  uploaderId: input.uploader.uid,
  uploaderName: input.uploader.displayName,
  artifactType: input.artifactType ?? 'FREEFORM',
  version: 1,
  createdAt: mockTimestamp(daysFromNow(-input.daysAgo)),
  syncTimestamp: mockTimestamp(daysFromNow(-input.daysAgo)),
  sourceChatTitle: input.sourceChatTitle ?? null,
}) as WorkspaceFileMetadata

export const DEMO_UPLOADS: WorkspaceFileMetadata[] = [
  buildFile({ id: 'u-1', fileName: 'chen-rivera-run-sheet.pdf', mimeType: 'application/pdf', sizeBytes: 480_000, uploader: KAI, daysAgo: 1, artifactType: 'RUNSHEET' }),
  buildFile({ id: 'u-2', fileName: 'patel-sharma-moodboard-v2.png', mimeType: 'image/png', sizeBytes: 3_200_000, uploader: JULES, daysAgo: 2, artifactType: 'DESIGN' }),
  buildFile({ id: 'u-3', fileName: 'meridian-florals-contract.pdf', mimeType: 'application/pdf', sizeBytes: 720_000, uploader: KAI, daysAgo: 4, artifactType: 'CONTRACT' }),
  buildFile({ id: 'u-4', fileName: 'moretti-kim-guest-list.csv', mimeType: 'text/csv', sizeBytes: 44_000, uploader: NOOR, daysAgo: 5, artifactType: 'GUEST_LIST' }),
  buildFile({ id: 'u-5', fileName: 'venue-walkthrough-notes.md', mimeType: 'text/markdown', sizeBytes: 12_000, uploader: SAM, daysAgo: 7 }),
  buildFile({ id: 'u-6', fileName: 'napa-chateau-invoice-DEMO.pdf', mimeType: 'application/pdf', sizeBytes: 210_000, uploader: NOOR, daysAgo: 10, artifactType: 'INVOICE' }),
  buildFile({ id: 'u-7', fileName: 'johnson-wu-color-story.jpg', mimeType: 'image/jpeg', sizeBytes: 1_800_000, uploader: JULES, daysAgo: 12, artifactType: 'DESIGN' }),
  buildFile({ id: 'u-8', fileName: 'liu-anders-vendor-shortlist.xlsx', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', sizeBytes: 88_000, uploader: SAM, daysAgo: 15 }),
]

export const DEMO_STORAGE_QUOTA: WorkspaceStorageQuota = {
  usedBytes: DEMO_UPLOADS.reduce((sum, f) => sum + f.sizeBytes, 0),
  reservedBytes: 0,
  baseLimitBytes: 25 * 1024 * 1024 * 1024,
  burstBytes: 0,
  totalBytes: 25 * 1024 * 1024 * 1024,
  isAllowed: true,
}

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

const dailyBuckets = Array.from({ length: 60 }, (_, index) => {
  const date = daysFromNow(-59 + index)
  const dayKey = date.toISOString().slice(0, 10)
  // Realistic ebb-and-flow — spikes on wedding-adjacent dates
  const baseSpend = 45_000 + Math.round(80_000 * Math.abs(Math.sin(index / 3.4)))
  return {
    date: dayKey,
    spend: baseSpend + (index % 7 === 0 ? 120_000 : 0),
    entries: 3 + (index % 5),
  }
})

export const DEMO_BUDGET_SUMMARY: WorkspaceBudgetSummary = {
  workspaceId: WORKSPACE_ID,
  currency: 'USD',
  totalBudgetLimit: 455_000_00,
  totalActualSpend: 116_000_00,
  totalEstimatedCost: 425_000_00,
  dailyBuckets,
  topSpenders: [
    { entityId: 'goal-chen', entityType: 'GOAL', title: 'Chen × Rivera — Golden Gate Club', spend: 41_200_00, budgetLimit: 65_000_00, percentConsumed: 0.63 },
    { entityId: 'goal-patel', entityType: 'GOAL', title: 'Patel × Sharma — Vineyard Estate', spend: 39_400_00, budgetLimit: 92_000_00, percentConsumed: 0.43 },
    { entityId: 'goal-moretti', entityType: 'GOAL', title: 'Moretti × Kim — Palace of Fine Arts', spend: 21_000_00, budgetLimit: 78_000_00, percentConsumed: 0.27 },
    { entityId: 'goal-johnson', entityType: 'GOAL', title: 'Johnson × Wu — Bay Front Loft', spend: 9_800_00, budgetLimit: 54_000_00, percentConsumed: 0.18 },
    { entityId: 'goal-liu', entityType: 'GOAL', title: 'Liu × Anders — Sonoma Barn', spend: 3_200_00, budgetLimit: 48_000_00, percentConsumed: 0.07 },
    { entityId: 'goal-garcia', entityType: 'GOAL', title: 'García × Nakamura — Napa Chateau', spend: 1_400_00, budgetLimit: 118_000_00, percentConsumed: 0.01 },
  ],
  overBudgetCount: 0,
  breachCount: 0,
  overrideCount: 0,
  lastUpdatedAt: mockTimestamp(new Date()),
}

// ---------------------------------------------------------------------------
// Quotas — one active + one archived per key team member
// ---------------------------------------------------------------------------

const buildQuota = (input: {
  id: string
  assignee: DemoMember
  status: 'ACTIVE' | 'ARCHIVED'
  targets: Quota['targets']
  startDaysAgo: number
  endDaysFromNow: number
}): Quota => ({
  id: input.id,
  workspaceId: WORKSPACE_ID,
  assigneeId: input.assignee.uid,
  targets: input.targets,
  startDate: mockTimestamp(daysFromNow(-input.startDaysAgo)),
  endDate: mockTimestamp(daysFromNow(input.endDaysFromNow)),
  status: input.status,
  createdAt: mockTimestamp(daysFromNow(-input.startDaysAgo)),
  createdBy: 'aria-founder',
  updatedAt: mockTimestamp(daysFromNow(-1)),
  updatedBy: 'aria-founder',
})

export const DEMO_QUOTAS_ACTIVE: Quota[] = [
  buildQuota({ id: 'q-active-kai', assignee: KAI, status: 'ACTIVE', startDaysAgo: 20, endDaysFromNow: 10,
    targets: { tasks_completed: { targetValue: 30, currentValue: 22 }, weighted_progress: { targetValue: 100, currentValue: 74 } } }),
  buildQuota({ id: 'q-active-jules', assignee: JULES, status: 'ACTIVE', startDaysAgo: 20, endDaysFromNow: 10,
    targets: { checklists_done: { targetValue: 40, currentValue: 31 } } }),
  buildQuota({ id: 'q-active-sam', assignee: SAM, status: 'ACTIVE', startDaysAgo: 15, endDaysFromNow: 15,
    targets: { tasks_completed: { targetValue: 24, currentValue: 12 }, checklists_done: { targetValue: 20, currentValue: 9 } } }),
  buildQuota({ id: 'q-active-noor', assignee: NOOR, status: 'ACTIVE', startDaysAgo: 15, endDaysFromNow: 15,
    targets: { sales_units: { targetValue: 80, currentValue: 62 } } }),
]

export const DEMO_QUOTAS_HISTORY: Quota[] = [
  buildQuota({ id: 'q-hist-aria', assignee: ARIA, status: 'ARCHIVED', startDaysAgo: 60, endDaysFromNow: -30,
    targets: { tasks_completed: { targetValue: 40, currentValue: 44 } } }),
  buildQuota({ id: 'q-hist-kai', assignee: KAI, status: 'ARCHIVED', startDaysAgo: 60, endDaysFromNow: -30,
    targets: { tasks_completed: { targetValue: 25, currentValue: 27 }, weighted_progress: { targetValue: 100, currentValue: 100 } } }),
]

// ---------------------------------------------------------------------------
// Stock — rental inventory
// ---------------------------------------------------------------------------

const buildStock = (input: {
  id: string
  name: string
  sku: string
  quantity: number
  reserved: number
  unit: string
  location: string
}): StockItem => ({
  id: input.id,
  workspaceId: WORKSPACE_ID,
  name: input.name,
  sku: input.sku,
  quantity: input.quantity,
  reservedQuantity: input.reserved,
  unit: input.unit,
  location: input.location,
  status: input.reserved >= input.quantity && input.quantity > 0 ? 'In-Use' : 'Available',
  lastUpdated: mockTimestamp(daysFromNow(-1)),
  linkedTaskIds: [],
})

export const DEMO_STOCK: StockItem[] = [
  buildStock({ id: 's-1', name: 'Chiavari chair (gold)', sku: 'CHAIR-GOLD', quantity: 240, reserved: 180, unit: 'each', location: 'Warehouse A · Bay 3' }),
  buildStock({ id: 's-2', name: 'Round banquet table (60")', sku: 'TABLE-R60', quantity: 40, reserved: 24, unit: 'each', location: 'Warehouse A · Bay 1' }),
  buildStock({ id: 's-3', name: 'Ivory linen tablecloth (120")', sku: 'LINEN-IVR-120', quantity: 120, reserved: 60, unit: 'each', location: 'Warehouse B · Rack 4' }),
  buildStock({ id: 's-4', name: 'String light strand (25ft, warm white)', sku: 'LIGHT-25W', quantity: 30, reserved: 12, unit: 'strand', location: 'Warehouse B · Shelf 7' }),
  buildStock({ id: 's-5', name: 'Ceremony arch (birch, freestanding)', sku: 'ARCH-BIRCH', quantity: 3, reserved: 3, unit: 'each', location: 'Warehouse A · Bay 6' }),
  buildStock({ id: 's-6', name: 'Champagne flute (rental grade)', sku: 'FLUTE-CHMP', quantity: 400, reserved: 220, unit: 'each', location: 'Warehouse B · Rack 2' }),
  buildStock({ id: 's-7', name: 'Portable PA system (Bose L1 Pro)', sku: 'PA-L1PRO', quantity: 4, reserved: 2, unit: 'each', location: 'Office · Storage closet' }),
  buildStock({ id: 's-8', name: 'Vintage rug runner (12ft)', sku: 'RUG-VINT-12', quantity: 6, reserved: 1, unit: 'each', location: 'Warehouse B · Loft' }),
]

// ---------------------------------------------------------------------------
// Forms — one intake + one vendor response, with responses
// ---------------------------------------------------------------------------

export const DEMO_FORMS: FormDefinition[] = [
  {
    id: 'form-intake',
    workspaceId: WORKSPACE_ID,
    title: 'Couple intake questionnaire',
    titleSearch: 'couple intake questionnaire',
    titlePrefixes: ['couple', 'intake', 'questionnaire'],
    description: 'Sent to every couple after signing the planning contract. Powers the initial vision + logistics brief.',
    fields: [
      { id: 'f-couple', type: 'TEXT', label: 'Couple name(s)', required: true, order: 0, validation: {} },
      { id: 'f-date', type: 'DATE', label: 'Target wedding date', required: true, order: 1, validation: {} },
      { id: 'f-guests', type: 'NUMBER', label: 'Expected guest count', required: true, order: 2, validation: { min: 0 } },
      { id: 'f-budget', type: 'NUMBER', label: 'Total budget (USD)', required: false, order: 3, validation: { min: 0 } },
      { id: 'f-style', type: 'DROPDOWN', label: 'Preferred style', required: false, order: 4, options: [
        { id: 'o1', label: 'Modern', value: 'modern' },
        { id: 'o2', label: 'Rustic', value: 'rustic' },
        { id: 'o3', label: 'Classic', value: 'classic' },
        { id: 'o4', label: 'Boho', value: 'boho' },
      ], validation: {} },
      { id: 'f-notes', type: 'TEXT', label: 'Anything the team should know?', required: false, order: 5, multiline: true, validation: {} },
    ],
    logic: { version: 1, rules: [] },
    theme: { accentColor: '#db2777', density: 'COMFORTABLE' },
    lifecycleStatus: 'ACTIVE',
    submissionCount: 6,
    lastSubmissionResponseId: 'resp-intake-1',
    deadline: null,
    isLocked: false,
    createdBy: 'aria-founder',
    createdAt: mockTimestamp(daysFromNow(-90)),
    updatedAt: mockTimestamp(daysFromNow(-14)),
  },
  {
    id: 'form-vendor-brief',
    workspaceId: WORKSPACE_ID,
    title: 'Vendor onboarding brief',
    titleSearch: 'vendor onboarding brief',
    titlePrefixes: ['vendor', 'onboarding', 'brief'],
    description: 'For new photographers, florists, and caterers — captures liability, capacity, and pricing tiers.',
    fields: [
      { id: 'v-name', type: 'TEXT', label: 'Business name', required: true, order: 0, validation: {} },
      { id: 'v-contact', type: 'TEXT', label: 'Primary contact', required: true, order: 1, validation: {} },
      { id: 'v-service', type: 'DROPDOWN', label: 'Service category', required: true, order: 2, options: [
        { id: 'sv1', label: 'Photography', value: 'photo' },
        { id: 'sv2', label: 'Videography', value: 'video' },
        { id: 'sv3', label: 'Florals', value: 'florals' },
        { id: 'sv4', label: 'Catering', value: 'catering' },
        { id: 'sv5', label: 'Music / DJ', value: 'music' },
      ], validation: {} },
      { id: 'v-cap', type: 'NUMBER', label: 'Max concurrent events', required: false, order: 3, validation: { min: 1 } },
      { id: 'v-liability', type: 'CHECKBOX', label: 'Has $2M+ liability insurance', required: true, order: 4, validation: {} },
    ],
    logic: { version: 1, rules: [] },
    theme: { accentColor: '#0ea5e9', density: 'COMPACT' },
    lifecycleStatus: 'ACTIVE',
    submissionCount: 12,
    lastSubmissionResponseId: 'resp-vendor-1',
    deadline: null,
    isLocked: false,
    createdBy: 'kai-lead',
    createdAt: mockTimestamp(daysFromNow(-120)),
    updatedAt: mockTimestamp(daysFromNow(-30)),
  },
]

export const DEMO_FORM_RESPONSES: Record<string, FormResponse[]> = {
  'form-intake': [
    {
      id: 'resp-intake-1',
      workspaceId: WORKSPACE_ID,
      formId: 'form-intake',
      submittedBy: 'demo-couple-1',
      submittedByName: 'Alicia Chen & Diego Rivera',
      data: { 'f-couple': 'Chen × Rivera', 'f-date': '2026-08-22', 'f-guests': 180, 'f-budget': 65000, 'f-style': 'classic', 'f-notes': 'Ceremony outdoors, reception inside.' },
      status: 'REVIEWED',
      submittedAt: mockTimestamp(daysFromNow(-40)),
    },
    {
      id: 'resp-intake-2',
      workspaceId: WORKSPACE_ID,
      formId: 'form-intake',
      submittedBy: 'demo-couple-2',
      submittedByName: 'Anaya Patel & Rohan Sharma',
      data: { 'f-couple': 'Patel × Sharma', 'f-date': '2026-09-14', 'f-guests': 250, 'f-budget': 92000, 'f-style': 'modern', 'f-notes': 'Two-day event, mehndi Friday, ceremony Saturday.' },
      status: 'REVIEWED',
      submittedAt: mockTimestamp(daysFromNow(-32)),
    },
  ],
  'form-vendor-brief': [
    {
      id: 'resp-vendor-1',
      workspaceId: WORKSPACE_ID,
      formId: 'form-vendor-brief',
      submittedBy: 'demo-vendor-1',
      submittedByName: 'Meridian Florals',
      data: { 'v-name': 'Meridian Florals', 'v-contact': 'ops@meridian.example.test', 'v-service': 'florals', 'v-cap': 3, 'v-liability': true },
      status: 'REVIEWED',
      submittedAt: mockTimestamp(daysFromNow(-60)),
    },
  ],
}

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

const buildMeeting = (input: {
  id: string
  title: string
  description: string
  status: MeetingRecord['status']
  provider: MeetingRecord['sourceProvider']
  daysFromNow: number
  durationMinutes: number
  agenda: string
  participants: DemoMember[]
}): MeetingRecord => ({
  id: input.id,
  workspaceId: WORKSPACE_ID,
  title: input.title,
  description: input.description,
  status: input.status,
  sourceProvider: input.provider,
  providerMeetingId: `demo-${input.id}`,
  joinUrl: `https://demo.example.test/join/${input.id}`,
  scheduledAt: mockTimestamp(daysFromNow(input.daysFromNow)),
  durationMinutes: input.durationMinutes,
  timezone: 'America/Los_Angeles',
  agendaMarkdown: input.agenda,
  agendaNodes: [],
  participants: input.participants.map((member) => ({
    userId: member.uid,
    displayName: member.displayName,
    rsvpStatus: 'ACCEPTED',
    external: false,
  })),
  projectId: null,
  connectionId: null,
  connectionErrorCode: null,
  createdBy: 'aria-founder',
  createdAt: mockTimestamp(daysFromNow(-14)),
  updatedAt: mockTimestamp(daysFromNow(-2)),
  lastStatusSyncAt: mockTimestamp(daysFromNow(-2)),
})

export const DEMO_MEETINGS: MeetingRecord[] = [
  buildMeeting({
    id: 'mtg-1', title: 'Chen × Rivera — final vendor walkthrough',
    description: 'Confirm timing, floor plan, and vendor logistics on-site.',
    status: 'SCHEDULED', provider: 'ZOOM', daysFromNow: 2, durationMinutes: 60,
    agenda: '1. Venue timing walkthrough\n2. Vendor arrival slots\n3. Contingency plans\n4. Final Q&A',
    participants: [ARIA, KAI, JULES],
  }),
  buildMeeting({
    id: 'mtg-2', title: 'Weekly team standup',
    description: 'Every Monday — priorities, blockers, budget check.',
    status: 'SCHEDULED', provider: 'GOOGLE_MEET', daysFromNow: 4, durationMinutes: 30,
    agenda: '1. Wins from last week\n2. This week priorities\n3. Blockers\n4. Budget review',
    participants: DEMO_MEMBERS.slice(0, 8),
  }),
  buildMeeting({
    id: 'mtg-3', title: 'Patel × Sharma — design review',
    description: 'Mood board V2 walkthrough with the couple.',
    status: 'SCHEDULED', provider: 'ZOOM', daysFromNow: 7, durationMinutes: 45,
    agenda: '1. Mood board V2 walkthrough\n2. Palette + material sign-off\n3. Next steps',
    participants: [JULES, ARIA],
  }),
  buildMeeting({
    id: 'mtg-4', title: 'Napa Chateau — invoice follow-up',
    description: 'Escalate overdue deposit invoice.',
    status: 'SCHEDULED', provider: 'HUDDLE', daysFromNow: 1, durationMinutes: 15,
    agenda: '1. Invoice status\n2. Wire timing\n3. Contract implications',
    participants: [NOOR, ARIA],
  }),
  buildMeeting({
    id: 'mtg-5', title: 'Kickoff — García × Nakamura',
    description: 'Initial planning session for Feb 2027 event.',
    status: 'COMPLETED', provider: 'ZOOM', daysFromNow: -14, durationMinutes: 90,
    agenda: '1. Vision + priorities\n2. Budget framework\n3. Venue shortlist\n4. Timeline',
    participants: [ARIA, KAI, JULES, SAM],
  }),
  buildMeeting({
    id: 'mtg-6', title: 'Vendor sync — Meridian Florals',
    description: 'Quarterly sync with our primary florist.',
    status: 'COMPLETED', provider: 'GOOGLE_MEET', daysFromNow: -7, durationMinutes: 45,
    agenda: '1. Seasonal availability\n2. Q4 pricing\n3. Upcoming events pipeline',
    participants: [KAI, ARIA],
  }),
]

// ---------------------------------------------------------------------------
// Automations
// ---------------------------------------------------------------------------

const buildAutomation = (input: {
  id: string
  name: string
  description: string
  status: 'ACTIVE' | 'PAUSED' | 'DISABLED'
  trigger: AutomationDefinition['trigger']
  actions: AutomationDefinition['actions']
  lastRunDaysAgo: number
  lastRunOutcome: 'SUCCESS' | 'FAILED' | 'SKIPPED' | 'SIMULATED'
}): AutomationDefinition => ({
  id: input.id,
  workspaceId: WORKSPACE_ID,
  name: input.name,
  description: input.description,
  trigger: input.trigger,
  actions: input.actions,
  status: input.status,
  createdBy: 'aria-founder',
  createdAt: mockTimestamp(daysFromNow(-100)),
  updatedAt: mockTimestamp(daysFromNow(-14)),
  lastRun: mockTimestamp(daysFromNow(-input.lastRunDaysAgo)),
  lastRunOutcome: input.lastRunOutcome,
})

export const DEMO_AUTOMATIONS: AutomationDefinition[] = [
  buildAutomation({
    id: 'auto-1', name: 'Vendor 7-day reminder',
    description: 'Post to #vendors and notify Kai 7 days before every wedding date.',
    status: 'ACTIVE',
    trigger: { type: 'DATE_REACHED', config: { cron: '0 9 * * *' } },
    actions: [
      { id: 'a-1', type: 'SEND_CHAT_MESSAGE', config: { channelId: 'ch-vendors', text: 'Reminder: vendor confirmations for this week\'s events are due.' } as never },
      { id: 'a-2', type: 'NOTIFY_USER', config: { userId: 'kai-lead', text: 'Vendor confirmations pending' } as never },
    ],
    lastRunDaysAgo: 1, lastRunOutcome: 'SUCCESS',
  }),
  buildAutomation({
    id: 'auto-2', name: 'Client intake → create Goal',
    description: 'When couple intake form is submitted, create a Goal on the Board.',
    status: 'ACTIVE',
    trigger: { type: 'FORM_SUBMITTED', config: { formId: 'form-intake' } },
    actions: [
      { id: 'a-1', type: 'CREATE_NODE', config: { title: 'New wedding: {{couple}}', nodeKind: 'GOAL' } as never },
      { id: 'a-2', type: 'NOTIFY_USER', config: { userId: 'aria-founder', text: 'New client intake — review and assign lead planner' } as never },
    ],
    lastRunDaysAgo: 6, lastRunOutcome: 'SUCCESS',
  }),
  buildAutomation({
    id: 'auto-3', name: 'Weekly recap to founder',
    description: 'Every Friday at 5pm, DM Aria with progress + spend for the week.',
    status: 'ACTIVE',
    trigger: { type: 'DATE_REACHED', config: { cron: '0 17 * * 5' } },
    actions: [
      { id: 'a-1', type: 'NOTIFY_USER', config: { userId: 'aria-founder', text: 'Weekly recap ready' } as never },
    ],
    lastRunDaysAgo: 3, lastRunOutcome: 'SUCCESS',
  }),
  buildAutomation({
    id: 'auto-4', name: 'Overdue invoice → escalate',
    description: 'If a vendor invoice checklist item stays uncompleted past due date, escalate to Aria.',
    status: 'ACTIVE',
    trigger: { type: 'CHECKLIST_COMPLETED', config: {} },
    actions: [
      { id: 'a-1', type: 'NOTIFY_USER', config: { userId: 'aria-founder', text: 'Overdue invoice escalation' } as never },
      { id: 'a-2', type: 'SEND_CHAT_MESSAGE', config: { channelId: 'ch-vendors', text: '⚠️ Overdue invoice needs attention' } as never },
    ],
    lastRunDaysAgo: 12, lastRunOutcome: 'SIMULATED',
  }),
  buildAutomation({
    id: 'auto-5', name: 'Photo upload → tag couple',
    description: 'On upload with couple keyword in filename, tag couple Goal and auto-categorize.',
    status: 'PAUSED',
    trigger: { type: 'UPLOAD_COMPLETED', config: { providerId: 'ANY' } },
    actions: [
      { id: 'a-1', type: 'UPDATE_FIELD', config: { field: 'tags', operation: 'add' } as never },
    ],
    lastRunDaysAgo: 30, lastRunOutcome: 'SUCCESS',
  }),
]

export const DEMO_AUTOMATION_RUNS: AutomationRun[] = DEMO_AUTOMATIONS.slice(0, 4).map((auto, index) => ({
  id: `run-${auto.id}`,
  automationId: auto.id,
  workspaceId: WORKSPACE_ID,
  triggeredBy: 'system',
  triggerEventSummary: auto.trigger.type,
  outcome: auto.lastRunOutcome ?? 'SUCCESS',
  startedAt: mockTimestamp(daysFromNow(-(index + 1))),
  finishedAt: mockTimestamp(daysFromNow(-(index + 1))),
} as unknown as AutomationRun))

// ---------------------------------------------------------------------------
// Registry — one place to look up demo data for a workspace ID
// ---------------------------------------------------------------------------

export type DemoBudgetLedgerRow = {
  id: string
  createdAtMs: number
  action: string
  activityType: string
  actorId: string
  actorName: string
  targetId: string
  targetTitle: string
  targetType: 'GOAL' | 'TASK' | 'CHECKLIST'
  summary: string
  amount: number
  currency: string
  category: string
}

const buildLedgerRow = (input: {
  id: string
  daysAgo: number
  actor: DemoMember
  targetTitle: string
  targetType: 'GOAL' | 'TASK' | 'CHECKLIST'
  amountMinor: number
  category: string
  summary: string
}): DemoBudgetLedgerRow => ({
  id: input.id,
  createdAtMs: Date.now() - input.daysAgo * 86_400_000,
  action: 'expense.log',
  activityType: 'BUDGET_EXPENSE',
  actorId: input.actor.uid,
  actorName: input.actor.displayName,
  targetId: `${input.id}-target`,
  targetTitle: input.targetTitle,
  targetType: input.targetType,
  summary: input.summary,
  amount: input.amountMinor,
  currency: 'USD',
  category: input.category,
})

export const DEMO_BUDGET_LEDGER: DemoBudgetLedgerRow[] = [
  buildLedgerRow({ id: 'led-1', daysAgo: 1, actor: DEMO_MEMBERS[4], targetTitle: 'Chen × Rivera — Golden Gate Club',
    targetType: 'GOAL', amountMinor: 8_500_00, category: 'Vendors', summary: 'Deposit — Meridian Florals' }),
  buildLedgerRow({ id: 'led-2', daysAgo: 2, actor: DEMO_MEMBERS[1], targetTitle: 'Vendors and contracts',
    targetType: 'TASK', amountMinor: 6_500_00, category: 'Vendors', summary: 'Photographer + videographer deposit' }),
  buildLedgerRow({ id: 'led-3', daysAgo: 3, actor: DEMO_MEMBERS[3], targetTitle: 'Confirm catering menu tasting',
    targetType: 'CHECKLIST', amountMinor: 2_400_00, category: 'Materials', summary: 'Tasting materials + venue kitchen fee' }),
  buildLedgerRow({ id: 'led-4', daysAgo: 4, actor: DEMO_MEMBERS[4], targetTitle: 'Patel × Sharma — Vineyard Estate',
    targetType: 'GOAL', amountMinor: 12_000_00, category: 'Vendors', summary: 'Venue reservation balance' }),
  buildLedgerRow({ id: 'led-5', daysAgo: 5, actor: DEMO_MEMBERS[2], targetTitle: 'Lock florist arrangement design',
    targetType: 'CHECKLIST', amountMinor: 4_200_00, category: 'Vendors', summary: 'Design retainer' }),
  buildLedgerRow({ id: 'led-6', daysAgo: 6, actor: DEMO_MEMBERS[3], targetTitle: 'Send save-the-dates',
    targetType: 'CHECKLIST', amountMinor: 780_00, category: 'Materials', summary: 'Print + mail' }),
  buildLedgerRow({ id: 'led-7', daysAgo: 7, actor: DEMO_MEMBERS[1], targetTitle: 'Sign DJ and MC contracts',
    targetType: 'CHECKLIST', amountMinor: 2_900_00, category: 'Vendors', summary: 'DJ + MC contract deposit' }),
  buildLedgerRow({ id: 'led-8', daysAgo: 10, actor: DEMO_MEMBERS[4], targetTitle: 'Moretti × Kim — Palace of Fine Arts',
    targetType: 'GOAL', amountMinor: 18_000_00, category: 'Vendors', summary: 'Venue reservation deposit' }),
  buildLedgerRow({ id: 'led-9', daysAgo: 12, actor: DEMO_MEMBERS[0], targetTitle: 'Guest experience and logistics',
    targetType: 'TASK', amountMinor: 3_400_00, category: 'Travel', summary: 'Airport transfers block booking' }),
  buildLedgerRow({ id: 'led-10', daysAgo: 14, actor: DEMO_MEMBERS[3], targetTitle: 'Confirm hotel block',
    targetType: 'CHECKLIST', amountMinor: 1_100_00, category: 'Materials', summary: 'Welcome bag inserts + printing' }),
  buildLedgerRow({ id: 'led-11', daysAgo: 18, actor: DEMO_MEMBERS[1], targetTitle: 'Johnson × Wu — Bay Front Loft',
    targetType: 'GOAL', amountMinor: 9_800_00, category: 'Vendors', summary: 'Venue reservation + AV' }),
  buildLedgerRow({ id: 'led-12', daysAgo: 22, actor: DEMO_MEMBERS[2], targetTitle: 'Book photographer + videographer',
    targetType: 'CHECKLIST', amountMinor: 6_500_00, category: 'Vendors', summary: 'Full-day + secondary' }),
  buildLedgerRow({ id: 'led-13', daysAgo: 27, actor: DEMO_MEMBERS[0], targetTitle: 'Liu × Anders — Sonoma Barn',
    targetType: 'GOAL', amountMinor: 3_200_00, category: 'Vendors', summary: 'Venue holding deposit' }),
  buildLedgerRow({ id: 'led-14', daysAgo: 30, actor: DEMO_MEMBERS[3], targetTitle: 'García × Nakamura — Napa Chateau',
    targetType: 'GOAL', amountMinor: 1_400_00, category: 'Travel', summary: 'Site visit travel' }),
  buildLedgerRow({ id: 'led-15', daysAgo: 45, actor: DEMO_MEMBERS[1], targetTitle: 'Vendors and contracts',
    targetType: 'TASK', amountMinor: 5_800_00, category: 'Vendors', summary: 'Bulk contract insurance' }),
]

export type DemoWorkspaceData = {
  members: DemoMember[]
  channels: Channel[]
  messagesByChannelId: Map<string, Message[]>
  workspaceQuota: WorkspaceQuota
  uploads: WorkspaceFileMetadata[]
  storageQuota: WorkspaceStorageQuota
  budgetSummary: WorkspaceBudgetSummary
  budgetLedger: DemoBudgetLedgerRow[]
  quotasActive: Quota[]
  quotasHistory: Quota[]
  stock: StockItem[]
  forms: FormDefinition[]
  formResponses: Record<string, FormResponse[]>
  meetings: MeetingRecord[]
  automations: AutomationDefinition[]
  automationRuns: AutomationRun[]
}

const messagesByChannelId = new Map<string, Message[]>()
for (const msg of DEMO_MESSAGES) {
  const list = messagesByChannelId.get(msg.channelId) ?? []
  const { channelId: _drop, ...rest } = msg
  list.push(rest as Message)
  messagesByChannelId.set(msg.channelId, list)
}
// Sort each channel newest-last (chat surfaces render top-down = oldest first).
for (const [, list] of messagesByChannelId) {
  list.sort((a, b) => {
    const aMs = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0
    const bMs = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0
    return aMs - bMs
  })
}

export const ARIA_AND_CO_DEMO_DATA: DemoWorkspaceData = {
  members: DEMO_MEMBERS,
  channels: DEMO_CHANNELS,
  messagesByChannelId,
  workspaceQuota: DEMO_WORKSPACE_QUOTA,
  uploads: DEMO_UPLOADS,
  storageQuota: DEMO_STORAGE_QUOTA,
  budgetSummary: DEMO_BUDGET_SUMMARY,
  budgetLedger: DEMO_BUDGET_LEDGER,
  quotasActive: DEMO_QUOTAS_ACTIVE,
  quotasHistory: DEMO_QUOTAS_HISTORY,
  stock: DEMO_STOCK,
  forms: DEMO_FORMS,
  formResponses: DEMO_FORM_RESPONSES,
  meetings: DEMO_MEETINGS,
  automations: DEMO_AUTOMATIONS,
  automationRuns: DEMO_AUTOMATION_RUNS,
}

/** Central lookup — any context can call this to check for demo data. */
export const getDemoWorkspaceData = (workspaceId: string | null | undefined): DemoWorkspaceData | null => {
  if (workspaceId === WORKSPACE_ID) return ARIA_AND_CO_DEMO_DATA
  return null
}
